import { InspectionItemResultType } from './inspection.model';
import {
    NumericRangeConstraint,
    NumericRangeConstraintOperator,
    NumericRangeConstraints,
    BetweenConstraint,
    GreaterThanConstraint,
    LessThanConstraint,
} from './numeric-range-constraints.model';

/**
 * Type guards for constraint types
 */
export function isBetweenConstraint(constraint: NumericRangeConstraint): constraint is BetweenConstraint {
    return constraint.$type === NumericRangeConstraintOperator.Between;
}

export function isGreaterThanConstraint(constraint: NumericRangeConstraint): constraint is GreaterThanConstraint {
    return constraint.$type === NumericRangeConstraintOperator.GreaterThan;
}

export function isLessThanConstraint(constraint: NumericRangeConstraint): constraint is LessThanConstraint {
    return constraint.$type === NumericRangeConstraintOperator.LessThan;
}

/**
 * Helper function to validate a BetweenConstraint
 * Returns error message if invalid, null if valid
 */
export function validateBetweenConstraint(constraint: BetweenConstraint): string | null {
    if (constraint.minValue >= constraint.maxValue) {
        return `MinValue (${constraint.minValue}) must be less than MaxValue (${constraint.maxValue})`;
    }
    return null;
}

/**
 * Helper function to validate all constraints in a NumericRangeConstraints object
 * Returns error message if any constraint is invalid, null if all valid
 */
export function validateConstraints(constraints: NumericRangeConstraints | null | undefined): string | null {
    if (!constraints) {
        return null;
    }

    const allConstraints = [...(constraints.pass ?? []), ...(constraints.warning ?? []), ...(constraints.fail ?? [])];

    for (const constraint of allConstraints) {
        if (isBetweenConstraint(constraint)) {
            const error = validateBetweenConstraint(constraint);
            if (error) {
                return error;
            }
        }
    }

    return null;
}

/**
 * Represents a covered interval on the number line
 */
interface CoveredInterval {
    min: number;
    max: number;
    minInclusive: boolean;
    maxInclusive: boolean;
}

/**
 * Converts a constraint to its covered interval on the number line.
 * LessThan and GreaterThan are open-ended, so they extend to -Infinity/Infinity respectively.
 */
function constraintToInterval(constraint: NumericRangeConstraint): CoveredInterval | null {
    if (isBetweenConstraint(constraint)) {
        return { min: constraint.minValue, max: constraint.maxValue, minInclusive: true, maxInclusive: true };
    } else if (isGreaterThanConstraint(constraint)) {
        return { min: constraint.value, max: Infinity, minInclusive: false, maxInclusive: false };
    } else if (isLessThanConstraint(constraint)) {
        return { min: -Infinity, max: constraint.value, minInclusive: false, maxInclusive: false };
    }
    return null;
}

/**
 * Determines whether two intervals overlap or are contiguous (no gap between them).
 * Two intervals that share a boundary connect only if at least one side is inclusive at that point.
 */
function intervalsConnect(a: CoveredInterval, b: CoveredInterval): boolean {
    if (a.max < b.min || b.max < a.min) return false;
    if (a.max === b.min) return a.maxInclusive || b.minInclusive;
    if (b.max === a.min) return b.maxInclusive || a.minInclusive;
    return true;
}

/**
 * Produces the union of two connected intervals, preserving inclusive/exclusive boundaries.
 * When both intervals share the same min or max value, the result is inclusive if either side is.
 */
function mergeIntervals(a: CoveredInterval, b: CoveredInterval): CoveredInterval {
    // Determine the lower bound of the merged interval
    let min: number, minInclusive: boolean;
    if (a.min < b.min) { // a starts lower, take a's min boundary
        min = a.min;
        minInclusive = a.minInclusive;
    } else if (a.min > b.min) { // b starts lower, take b's min boundary
        min = b.min;
        minInclusive = b.minInclusive;
    } else { // same min value, inclusive if either is inclusive
        min = a.min;
        minInclusive = a.minInclusive || b.minInclusive;
    }

    // Determine the upper bound of the merged interval
    let max: number, maxInclusive: boolean;
    if (a.max > b.max) { // a ends higher, take a's max boundary
        max = a.max;
        maxInclusive = a.maxInclusive;
    } else if (a.max < b.max) { // b ends higher, take b's max boundary
        max = b.max;
        maxInclusive = b.maxInclusive;
    } else { // same max value, inclusive if either is inclusive
        max = a.max;
        maxInclusive = a.maxInclusive || b.maxInclusive;
    }

    return { min, max, minInclusive, maxInclusive };
}

/**
 * Detects gaps in constraint coverage across all categories (pass/warning/fail).
 * Returns the gap boundaries if found, or null if coverage is contiguous.
 * Only validates when at least two constraints exist across all categories.
 */
export function detectConstraintGaps(constraints: NumericRangeConstraints | null | undefined): { gapStart: number; gapEnd: number } | null {
    if (!constraints) {
        return null;
    }

    const allConstraints = [...(constraints.pass ?? []), ...(constraints.warning ?? []), ...(constraints.fail ?? [])];
    if (allConstraints.length < 2) {
        return null;
    }

    const intervals: CoveredInterval[] = [];
    for (const constraint of allConstraints) {
        const interval = constraintToInterval(constraint);
        if (interval) {
            intervals.push(interval);
        }
    }

    if (intervals.length === 0) {
        return null;
    }

    intervals.sort((a, b) => a.min - b.min || (a.minInclusive ? 0 : 1) - (b.minInclusive ? 0 : 1));

    const merged: CoveredInterval[] = [intervals[0]];
    for (let i = 1; i < intervals.length; i++) {
        const current = merged[merged.length - 1];
        const next = intervals[i];
        if (intervalsConnect(current, next)) {
            merged[merged.length - 1] = mergeIntervals(current, next);
        } else {
            return { gapStart: current.max, gapEnd: next.min };
        }
    }

    return null;
}

/**
 * Evaluates a numeric value against provided constraints
 * Returns 'pass', 'warning', 'fail', or null if no match
 */
export function evaluateConstraints(value: number, constraints: NumericRangeConstraints | null | undefined): InspectionItemResultType | null {
    if (!constraints) {
        return null;
    }
    if (checkConstraints(value, constraints.pass)) {
        return InspectionItemResultType.Pass;
    }
    if (checkConstraints(value, constraints.warning)) {
        return InspectionItemResultType.Warning;
    }
    if (checkConstraints(value, constraints.fail)) {
        return InspectionItemResultType.Fail;
    }
    return null;
}

/**
 * Checks if value satisfies any constraint in the provided list
 * Returns true if any constraint matches, false otherwise
 */
export function checkConstraints(value: number, constraintList: NumericRangeConstraint[] | undefined): boolean {
    if (!constraintList) {
        return false;
    }
    for (const constraint of constraintList) {
        if (isBetweenConstraint(constraint)) {
            if (value >= constraint.minValue && value <= constraint.maxValue) {
                return true;
            }
        } else if (isGreaterThanConstraint(constraint)) {
            if (value > constraint.value) {
                return true;
            }
        } else if (isLessThanConstraint(constraint)) {
            if (value < constraint.value) {
                return true;
            }
        }
    }
    return false;
}
